import { useEffect, useRef, useState } from "react";
import { Check, Loader2, MapPin, Upload } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { useFileDrop } from "@/hooks/useFileDrop";
import { cn } from "@/lib/utils";
import { PrimaryButton, SecondaryButton } from "@/components/shared/ui";
import { FileDropOverlay } from "@/components/shared/FileDropOverlay";
import { ExplainBox, SavedNote } from "@/components/applicant/ExplainBox";
import { StepActions } from "@/components/applicant/QuestionStep";
import { ErrorBox, FixBox, PassBox } from "@/components/applicant/stageUi";

const SLOW_CHECK_MS = 15000;
const ACCEPT = "application/pdf,image/*";

const Periods = ({ item }) => {
  const labels = Object.entries(item.period_labels || {});
  if (labels.length < 2) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {labels.map(([k, label]) => {
        const covered = item.covered.includes(k);
        return (
          <span key={k} className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium", covered ? "bg-success/10 text-success" : "bg-muted text-muted-foreground")}
            data-testid={`period-${item.key}-${k}`} data-covered={covered}>
            {covered && <Check className="size-3" strokeWidth={3} />} {label}
          </span>
        );
      })}
    </div>
  );
};

/** Upload a file for one checklist item; the server checks it instantly (Claude reads, rules.py decides). */
export const useUpload = (token, itemKey, onUpdated) => {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const send = async (file) => {
    setError("");
    setProgress(0);
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post(`/a/${token}/items/${itemKey}/upload`, body, { timeout: 60000, onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)) });
      await onUpdated();
    } catch (err) {
      setError(errorText(err, "That upload didn't work. Please try again."));
    } finally {
      setProgress(null);
    }
  };
  return { send, progress, error, setError };
};

const Checking = ({ name, slow, testid }) => (
  <div className="mt-6 rounded-xl bg-brand/40 p-4 dark:bg-brand/10" data-testid={testid}>
    <p className="flex items-center gap-2 text-sm font-medium"><Loader2 className="size-4 animate-spin" /> Checking {name || "your file"}…</p>
    <div className="scan-bar relative mt-3 h-px overflow-hidden" />
    <p className="mt-3 text-xs text-muted-foreground" data-testid={`${testid}-hint`}>
      {slow ? "Long files can take a minute or two. You can carry on and come back." : "This usually takes a few seconds."}
    </p>
  </div>
);

const Uploading = ({ progress, testid }) => (
  <div className="mt-6" data-testid={testid}>
    <p className="text-sm text-muted-foreground">{progress < 100 ? `Uploading… ${progress}%` : "Checking…"}</p>
    <div className="mt-1.5 h-0.5 overflow-hidden rounded-full bg-muted"><div className="h-full bg-foreground transition-[width]" style={{ width: `${progress}%` }} /></div>
  </div>
);

const useSlow = (active) => {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    if (!active) return undefined;
    const t = setTimeout(() => setSlow(true), SLOW_CHECK_MS);
    return () => clearTimeout(t);
  }, [active]);
  return slow;
};

const TextAction = ({ onClick, disabled, testid, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} className="w-full py-2 text-center text-sm font-medium text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-50" data-testid={testid}>{children}</button>
);

export const LaterLink = ({ onClick, testid }) => <TextAction onClick={onClick} testid={testid}>Do this later</TextAction>;

const DropZone = ({ onClick, disabled, again, multi, dragging, testid }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className={cn("mt-6 flex w-full flex-col items-center gap-3 rounded-2xl border-[1.5px] border-dashed bg-background px-6 py-9 text-center transition-colors hover:border-foreground/50 active:scale-[0.995] disabled:opacity-50",
      dragging ? "border-brand bg-brand/10" : "border-muted-foreground/35")}
    data-testid={testid} data-dragging={dragging ? "true" : "false"}>
    <span className="grid size-12 place-items-center rounded-full bg-brand text-brand-foreground"><Upload className="size-5" /></span>
    <span className="text-base font-semibold">{dragging ? "Drop to upload" : again ? "Upload the next file" : "Upload"}</span>
    <span className="text-sm text-muted-foreground">
      PDF or photo.{multi ? " You can pick several at once." : ""}<span className="hidden md:inline"> Or drag {multi ? "them" : "it"} here.</span>
    </span>
  </button>
);

/** One document request on its own screen: what we need, where to get it, one Upload button. */
export const DocumentItem = ({ item, token, onUpdated, onContinue, onLater, compact }) => {
  const input = useRef(null);
  const { send, progress, error, setError } = useUpload(token, item.key, onUpdated);
  const [explaining, setExplaining] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const latest = item.documents[item.documents.length - 1];
  const checking = latest?.status === "checking";
  const failed = latest && (latest.status === "needs_fixing" || latest.status === "needs_review");
  const busy = progress !== null || checking;
  const slow = useSlow(checking);
  const accepted = item.documents.filter((d) => d.accepted && d.summary);
  const status = item.complete ? "pass" : item.explained ? "warn" : "todo";
  const settled = item.complete || item.explained;

  const pick = () => input.current?.click();
  const upload = async (files) => {
    setExplaining(false);
    for (const f of files) await send(f); // eslint-disable-line no-await-in-loop
  };
  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (input.current) input.current.value = "";
    if (files.length) upload(files);
  };
  const drop = useFileDrop({
    page: true, accept: ACCEPT, multiple: item.multi, disabled: busy || explaining, onFiles: upload,
    onReject: () => setError("That type of file won't work here. Upload a PDF or a photo."),
  });
  const retryCheck = async () => {
    setRetrying(true);
    try {
      await api.post(`/a/${token}/documents/${latest.id}/retry`);
      await onUpdated();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setRetrying(false);
    }
  };

  const fixing = failed && progress === null;
  const Heading = compact ? "h2" : "h1";
  const actions = [
    settled && onContinue && <PrimaryButton key="c" size="lg" onClick={onContinue} testid={`item-continue-${item.key}`}>Continue</PrimaryButton>,
    checking && slow && onContinue && <PrimaryButton key="s" size="lg" onClick={onContinue} testid={`item-continue-checking-${item.key}`}>Continue, we'll keep checking</PrimaryButton>,
    fixing && <PrimaryButton key="u" size="lg" onClick={pick} disabled={busy} testid={`item-upload-different-${item.key}`}>Upload a different file</PrimaryButton>,
    fixing && <SecondaryButton key="e" size="lg" onClick={() => setExplaining(true)} disabled={busy} testid={`item-explain-button-${item.key}`}>{latest.note ? "Edit my note" : "Explain"}</SecondaryButton>,
    item.complete && <TextAction key="r" onClick={pick} disabled={busy} testid={`item-replace-button-${item.key}`}>Replace file</TextAction>,
    !settled && !busy && onLater && <LaterLink key="l" onClick={onLater} testid={`item-later-${item.key}`} />,
  ].filter(Boolean);

  return (
    <div className="rise" data-testid={`checklist-item-${item.key}`} data-status={status}>
      <span className="sr-only" data-testid={`item-tick-${item.key}`} data-complete={item.complete ? "true" : "false"}>{status === "pass" ? "Done" : status === "warn" ? "Sent with a note" : "To do"}</span>
      {item.complete && <span className="mb-3 inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success" data-testid={`item-accepted-${item.key}`}><Check className="size-3" strokeWidth={3} /> Accepted</span>}
      <Heading className={cn(compact ? "text-xl" : "text-[26px]", "font-semibold leading-tight tracking-tight")} data-testid={`item-title-${item.key}`}>{item.title}</Heading>
      {item.subtitle && <p className="mt-2 text-base leading-relaxed text-muted-foreground" data-testid={`item-subtitle-${item.key}`}>{item.subtitle}</p>}
      <p className="mt-4 text-base font-medium" data-testid={`item-detail-${item.key}`}>{item.detail}</p>
      <Periods item={item} />
      {item.where && !item.complete && (
        <div className="mt-5 flex gap-3 rounded-xl bg-background p-4 text-sm leading-relaxed ring-1 ring-border">
          <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <p data-testid={`item-where-${item.key}`}><span className="font-medium">Where to find it. </span>{item.where}</p>
        </div>
      )}

      {accepted.length > 0 && (
        <div className="mt-6 flex flex-col gap-2">
          {accepted.map((d) => <PassBox key={d.id} testid={`item-pass-${item.key}`}><span data-testid={`item-summary-${d.id}`}>{d.summary}</span></PassBox>)}
        </div>
      )}
      {checking && <Checking name={latest.original_filename} slow={slow} testid={`item-checking-${item.key}`} />}
      {progress !== null && !checking && <Uploading progress={progress} testid={`item-uploading-${item.key}`} />}
      {fixing && (
        <div className="mt-6" data-testid={`item-fix-${item.key}`}>
          <FixBox title={latest.note ? "Sent to the agent with your note" : "Needs fixing"}><span data-testid={`item-fix-message-${item.key}`}>{latest.fix_message}</span></FixBox>
          <SavedNote note={latest.note} testid={`item-note-${item.key}`} />
        </div>
      )}
      {latest?.status === "error" && (
        <ErrorBox className="mt-6 flex flex-col gap-2" testid={`item-error-${item.key}`}>
          {latest.error_message}
          <SecondaryButton size="sm" className="w-fit" onClick={retryCheck} isLoading={retrying} testid={`item-retry-${item.key}`}>Try again</SecondaryButton>
        </ErrorBox>
      )}
      {error && <ErrorBox className="mt-6" testid={`item-upload-error-${item.key}`}>{error}</ErrorBox>}

      <FileDropOverlay show={drop.dragging} title={item.multi ? "Drop to upload your files" : "Drop to upload your file"} text={item.title} testid={`item-drop-overlay-${item.key}`} />
      <input ref={input} type="file" accept={ACCEPT} multiple={item.multi} className="hidden" onChange={onFile} data-testid={`item-file-input-${item.key}`} />
      {explaining ? (
        <ExplainBox token={token} docId={latest.id} note={latest.note} onSaved={onUpdated} onClose={() => setExplaining(false)} testid={`item-explain-${item.key}`} />
      ) : (
        <>
          {!item.complete && !fixing && !busy && (
            <DropZone onClick={pick} again={accepted.length > 0} multi={item.multi} dragging={drop.dragging} testid={`item-upload-button-${item.key}`} />
          )}
          {actions.length > 0 && (compact ? <div className="mt-6 flex flex-col gap-2">{actions}</div> : <StepActions>{actions}</StepActions>)}
        </>
      )}
    </div>
  );
};
